import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import TypicalProblemList from './TypicalProblemList';
import { FindInPage } from '@material-ui/icons';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import * as actions from "./store/typicalProblemActions";

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    progressBarr: {
        width: '100%',
        height: 5
    }
});

const TypicalProblem = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions, onEditTypicalProblemVisibility, activeCustomerID } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, typicalProblemId: null, redirectPath: '' });

    const tableColumns = useMemo(() => {
        let visible = columns && columns['TypicalProblem'];

        const handleVisibilityChange = async data => {
            let visible = data.TypicalProblemVisibilities.length > 0 ? !data.TypicalProblemVisibilities[0].Visible : false;
            await onEditTypicalProblemVisibility({ ActiveCustomerID: activeCustomerID, TypicalProblemID: data.TypicalProblemID, Visible: visible });
            tableRef.current && tableRef.current.onQueryChange();
        };

        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'typical_problem_load') ?
                            <Tooltip title={translations["typical_problem_details"] || "Typical problem details"}>
                                <IconButton component="div" value={rowData.TypicalProblemID} aria-label={translations["typical_problem_details"] || "Typical problem details"}
                                    onClick={() => redirectHandler(`/settings/typical-problem/edit?typicalProblemID=${rowData.TypicalProblemID}`)}>
                                    <FindInPage />
                                </IconButton>
                            </Tooltip> : ''
                    }
                    {
                        hasPermission(permissions, 'typical_problem_visibility_update') ?
                            <Tooltip title={translations["typical_problem_visibility"] || "Typical problem visibility"}>
                                <IconButton component="div" value={rowData.TypicalProblemID} aria-label={translations["typical_problem_visibility"] || "Typical problem visibility"}
                                    onClick={() => handleVisibilityChange(rowData)}>
                                    {rowData.TypicalProblemVisibilities.length === 0 && <VisibilityIcon />}
                                    {rowData.TypicalProblemVisibilities.length > 0 && rowData.TypicalProblemVisibilities[0].Visible && <VisibilityIcon />}
                                    {rowData.TypicalProblemVisibilities.length > 0 && !rowData.TypicalProblemVisibilities[0].Visible && <VisibilityOffIcon />}
                                </IconButton>
                            </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["short_name"] || 'Short name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["long_name"] || 'Long name', field: 'LongName',
                hidden: visible ? visible['LongName'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LongName ? rowData.LongName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["grade"] || 'Grade', field: 'GradeID',
                hidden: visible ? visible['GradeID'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Grade && rowData.Grade.InternationalGrading ? rowData.Grade.InternationalGrading : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["code"] || 'Code', field: 'Code',
                hidden: visible ? visible['Code'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Code ? rowData.Code : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["grading_category"] || 'Grading category', field: 'GradingCategoryID',
                hidden: visible ? visible['GradingCategoryID'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.GradingCategory ? rowData.GradingCategory.Product === 'unknown' ? 'All' : rowData.GradingCategory.Product : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["system"] || 'System', field: 'System',
                hidden: visible ? visible['System'] : false,
                filtering: true,
                lookup: { '1': translations["system"] || 'System', '0': translations["custom"] || 'Custom' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{!rowData.CustomerID ? translations["system"] || 'System' : translations["custom"] || 'Custom'}</Typography>;
                }
            },
            {
                title: translations["visible"] || 'Visible', field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                filtering: true,
                lookup: { '1': translations["visible"] || 'Visible', '0': translations["invisible"] || 'Non visible' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.TypicalProblemVisibilities && rowData.TypicalProblemVisibilities.length === 0 ? translations["visible"] || 'Visible' :
                        rowData.TypicalProblemVisibilities?.length > 0 && rowData.TypicalProblemVisibilities[0].Visible ? translations["visible"] || 'Visible' : translations["invisible"] || 'Non visible'}</Typography>;
                }
            }
        ]
    }, [translations, activeCustomerID, onEditTypicalProblemVisibility, columns, permissions]);

    if (!hasAtLeastOnePermission(permissions, ['typical_problem_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, typicalProblemId = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, typicalProblemId: typicalProblemId, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const goToTypicalProblemsUpload = () => {
        createHistory();
        setRedirectOptions({ ...redirectOptions, redirect: true, TranslationsID: null, redirectPath: `/settings/typical-problem/upload` });
    };

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'typical_problem_list') ?
                <TypicalProblemList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                    goToTypicalProblemsUpload={goToTypicalProblemsUpload}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        onEditTypicalProblemVisibility: typicalProblem => dispatch(actions.editTypicalProblemVisibility(typicalProblem)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(TypicalProblem));
